<script lang="ts" setup>
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { useTranslations } from '~/composables/translations';
import type { FAQMenuContent } from '~/models/faq_models';

import vuIcon from '@ds/vuekit/src/components/vu-icon.vue';

const props = defineProps<{
  content: FAQMenuContent[];
  activeContent?: FAQMenuContent;
  module: string;
  isResponsive: boolean;
}>();
const active = ref(false);

const emit = defineEmits(['redirect']);
const { $i18n } = useTranslations(props.module);

const route = useRoute();
const setActive = () => {
  active.value = !active.value;
};
</script>
<template>
  <!-- Menu -->
  <div
    :class="['faq-menu blue-3--text flex flex-column grid-gap-10px pa-10px', { active: active }]"
    v-click-outside="() => (active = false)"
  >
    <div class="faq-category" v-for="item in content" :key="`faq-menu-category-${item.title}`">
      <a
        class="mpf-faq-category-title flex flex-row grid-gap-5px pointer"
        @click.prevent="emit('redirect', item.articles[0].url)"
      >
        <span
          :class="[
            'fonticon',
            {
              'fonticon-expand-down': activeContent?.title === item.title,
              'fonticon-expand-right': activeContent?.title !== item.title,
            },
          ]"
        />
        <span :class="`fonticon ${item.icon}`" />
        {{ $i18n(item.title) }}
      </a>
      <div
        class="ml-40px py-10px flex flex-column grid-gap-5px"
        v-if="activeContent?.title === item.title"
      >
        <div v-for="link in item.articles" :key="`faq-category-link-${link}`" class="flex-clamp-1">
          <a
            @click.prevent="emit('redirect', link.url)"
            :class="['pointer faq-link pa-5px', { active: route.path.includes(link.url) }]"
          >
            {{ $i18n(link.title) }}
          </a>
        </div>
      </div>
    </div>
    <vu-icon
      :class="['faq-responsive-btn flex align-items-center justify-center', { active: active }]"
      :icon="active ? 'chevron-left' : 'chevron-right'"
      v-if="isResponsive"
      color="primary"
      @click.prevent="setActive()"
    />
  </div>
</template>
<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;

.faq-menu {
  position: sticky;
  align-self: flex-start;
  top: 0;
  min-width: 300px;
  width: 300px;

  @include variables.for-sm {
    position: absolute;
    width: 45vw;
    height: 100vh;
    z-index: 2;
    top: 89px;
    left: 0;
    background-color: var(--grey-2);
    transform: translateX(-45vw);
    transition: transform ease-in-out 500ms;
    border-right: 1px solid var(--grey-3);
  }

  @include variables.for-xs {
    width: 100vw;
    transform: translateX(-100vw);
  }
}

.faq-menu.active {
  transform: translate(0);
}

.faq-responsive-btn {
  position: absolute;
  right: -40px;
  content: '\e140';
  font-family: entypo;
  height: 1.5rem;
  width: 1.5rem;
  color: black;
  line-height: 10px;
  border: 1px solid var(--grey-3);
  background-color: var(--grey-2);
  border-radius: 50px;

  top: 50%;
  bottom: 50%;
  margin: auto 0;
}
.faq-responsive-btn.active {
  @include variables.for-xs {
    right: 10px;
  }
}

.mpf-faq-category-title {
  text-transform: uppercase;
  font-size: 1.1em;
  cursor: pointer;
}

.faq-link.active {
  border-left: 1px solid #368ec4;
}
</style>
